import React, { useState, useEffect, useRef, useCallback } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import PenIcon from '@material-ui/icons/Check'
import SignaturePad from 'signature_pad'
import { IControlProps } from './types'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../OneplaceLibraryProvider'

export interface ICanvasProps {
  photoBase64Data: string
  templateBase64Data: string
  completed: boolean
}

export interface ICanvasControlProps
  extends IControlProps,
    IOneplaceLibraryContextProp {
  photoData: ICanvasProps
}

export const SIGNATURE_PAD_DELAY = 200

const CanvasControl: React.FC<ICanvasControlProps> = (
  props: ICanvasControlProps
) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [penColour, setPenColour] = useState('black')

  // the current state of the photo
  const [image, setImage] = useState<string>(props.photoData.photoBase64Data)

  useEffect(() => {
    setImage(props.photoData.photoBase64Data)
  }, [props.photoData])

  // Refs
  // the canvas for drawing
  const photoCanvasRef = useRef<HTMLCanvasElement>(null)

  // the thumbnail
  const photoImgRef = useRef<HTMLImageElement>(null)
  const signaturePadRef = useRef<SignaturePad | null>(null)

  // if the user has reset the photo?
  const resetBtnClickRef = useRef<boolean>(false)

  // the current photo aspect ratio
  const photoAspectRatioRef = useRef(1)

  // Calculate aspect ratio based on the loaded image dimensions.
  const calculatePhotoAspectRatio = useCallback(() => {
    if (photoImgRef.current) {
      const img = photoImgRef.current
      photoAspectRatioRef.current = img.height / img.width || 1
      console.log('photo aspect ratio', photoAspectRatioRef.current)
    }
  }, [])

  // Set the aspect ratio when the image loads or the image changes.
  useEffect(() => {
    const img = photoImgRef.current
    if (img) {
      if (img.complete) {
        calculatePhotoAspectRatio()
      } else {
        img.onload = calculatePhotoAspectRatio
      }
    }
  }, [image, calculatePhotoAspectRatio])

  const initSignaturePad = useCallback(() => {
    if (photoCanvasRef.current) {
      const canvas = photoCanvasRef.current
      const canvasWidth = canvas.parentElement!.offsetWidth
      const canvasHeight = canvasWidth * photoAspectRatioRef.current

      canvas.width = canvasWidth
      canvas.height = canvasHeight

      // Only initialize once.
      if (!signaturePadRef.current) {
        signaturePadRef.current = new SignaturePad(canvas)
      }

      signaturePadRef.current.penColor = penColour

      if (image) {
        void signaturePadRef.current.fromDataURL(image, { ratio: 1 })
      }
    }
  }, [image])

  useEffect(() => {
    if (dialogOpen) {
      // There is a short delay before the canvas is available
      const timeoutId = setTimeout(() => {
        try {
          initSignaturePad()
        } catch (e) {
          console.error('Unable to mount signature pad.', e)
        }
      }, SIGNATURE_PAD_DELAY)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [dialogOpen, initSignaturePad])

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    if (signaturePadRef.current) {
      signaturePadRef.current.off()
      signaturePadRef.current = null
    }
  }

  const handleSave = () => {
    // check the user drawing data
    const drawingData = signaturePadRef.current!.toData()
    if (drawingData.length === 0) {
      // no new drawing
      if (props.photoData.completed) {
        // user didn't draw anything in this edition
        // if user reset photo, change the photot data to template photo data also update the complete flag,
        // otherwise, the photo data remains unchanged
        props.form.setFieldValue(props.field.name, {
          photoBase64Data: resetBtnClickRef.current
            ? props.photoData.templateBase64Data
            : props.photoData.photoBase64Data,
          templateBase64Data: props.photoData.templateBase64Data,
          completed: resetBtnClickRef.current ? false : true
        })
        if (resetBtnClickRef.current) {
          // reset the thumbnail
          setImage(props.photoData.templateBase64Data)
        }
      }
    } else {
      // there's new drawing, set 'completed' flag to be true, also update the photo data
      // save image as png to match the signature question, so both use the same image type, this makes it easier to print them in the reports
      const newVal = signaturePadRef.current!.toDataURL('image/png')
      setImage(newVal || '')

      props.form.setFieldValue(props.field.name, {
        photoBase64Data: newVal,
        templateBase64Data: props.photoData.templateBase64Data,
        completed: true
      })
    }
    handleCloseDialog()
  }

  const handleSetPenColour = (colour: string) => {
    setPenColour(colour)
    if (signaturePadRef.current) {
      signaturePadRef.current.penColor = colour
    }
  }

  const handleResetCanvas = () => {
    if (signaturePadRef.current && props.photoData) {
      void signaturePadRef.current.fromDataURL(
        props.photoData.templateBase64Data,
        { ratio: 1 }
      )
      // clear the drawing data array so we can track if user adds new drawing after reseting the canvas
      signaturePadRef.current.clear()
      resetBtnClickRef.current = true
    }
  }

  const colours = [
    { colour: 'black', iconColour: 'white' },
    { colour: 'red', iconColour: 'white' },
    { colour: 'yellow', iconColour: 'black' }
  ]

  return (
    <div>
      <div>
        {image && (
          <img
            src={image}
            style={{
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
              border: '1px solid #AAA',
            }}
            ref={photoImgRef}
            onClick={props.disabled ? undefined : handleOpenDialog}
            alt='Photo'
          />
        )}
      </div>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{ style: { margin: 12, width: '100%', maxWidth: 700 } }}
      >
        <DialogContent>
          <div style={{ textAlign: 'right' }}>
            {colours.map((c) => {
              const buttonStyle = {
                width: 40,
                height: 40,
                marginLeft: 8,
                marginBottom: 8,
                minWidth: 0,
                minHeight: 0,
                boxShadow:
                  '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
                borderRadius: '50%',
                background: c.colour,
                color: c.iconColour
              }
              return (
                <Button
                  variant='contained'
                  style={buttonStyle}
                  key={c.colour}
                  onClick={() => handleSetPenColour(c.colour)}
                >
                  {penColour === c.colour && <PenIcon fontSize='small' />}
                </Button>
              )
            })}
          </div>
          <div style={{ border: '1px solid #000' }}>
            <canvas
              ref={photoCanvasRef}
              style={{ width: '100%', display: 'block' }}
            ></canvas>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color='primary' onClick={handleCloseDialog}>
            {props.ctx.i18next.t('cancel')}
          </Button>
          <Button style={{ color: '#F00' }} onClick={handleResetCanvas}>
            {props.ctx.i18next.t('clear')}
          </Button>
          <Button color='primary' onClick={handleSave}>
            {props.ctx.i18next.t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default withOneplaceLibraryContext(CanvasControl)
